/**
 * Centralized Payment Error Handler
 * Provides consistent error handling and user notifications across all payment flows.
 *
 * @module paymentErrorHandler
 */

import { toast } from 'react-toastify'
import {
  PAYMENT_ERROR_GENERIC,
  PAYMENT_ERROR_MESSAGES,
  PAYMENT_ERROR_TYPES,
} from './paymentConstants'

const TOAST_CONFIG = {
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  autoClose: 5000,
}

/**
 * Categorizes an error based on status code or error messages.
 *
 * @param {Error|Object} error - The error to categorize
 * @returns {string} One of PAYMENT_ERROR_TYPES
 */
export const getErrorType = (error) => {
  if (!error) return PAYMENT_ERROR_TYPES.UNKNOWN_ERROR

  const code = error.code
  const status = error.response?.status
  const message = error.message?.toLowerCase() || ''

  if (
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    status === 408 ||
    message.includes('timeout')
  ) {
    return PAYMENT_ERROR_TYPES.TIMEOUT_ERROR
  }

  if (
    code === 'ERR_NETWORK' ||
    message.includes('network') ||
    code === 'ENOTFOUND'
  ) {
    return PAYMENT_ERROR_TYPES.NETWORK_ERROR
  }

  if (status >= 400 && status < 500) {
    return PAYMENT_ERROR_TYPES.VALIDATION_ERROR
  }

  if (status >= 500) {
    return PAYMENT_ERROR_TYPES.GATEWAY_ERROR
  }

  return PAYMENT_ERROR_TYPES.UNKNOWN_ERROR
}

/**
 * Maps an error object to a user-friendly localized string.
 *
 * @param {Error|Object} error - The error object
 * @returns {string} Localized error message
 */
export const getErrorMessage = (error) => {
  const type = getErrorType(error)
  return PAYMENT_ERROR_MESSAGES[type] || PAYMENT_ERROR_GENERIC
}

/**
 * Logs structured error information to the console.
 *
 * @private
 * @param {string} type - Error category
 * @param {Error|Object} error - Original error
 * @param {Object} context - Metadata about where the error occurred
 */
const logPaymentError = (type, error, context) => {
  const isTransient =
    type === PAYMENT_ERROR_TYPES.NETWORK_ERROR ||
    type === PAYMENT_ERROR_TYPES.TIMEOUT_ERROR

  const logger = isTransient ? console.warn : console.error

  logger('[Payment Error]', {
    type,
    context,
    timestamp: new Date().toISOString(),
    message: error.message,
    responseData: error.response?.data,
    error,
  })
}

/**
 * Main entry point for handling payment-related errors.
 * Displays a toast message and logs structured data.
 *
 * @param {Error|Object} error - The error to handle
 * @param {Object} [context={}] - Execution context for debugging
 */
export const handlePaymentError = (error, context = {}) => {
  const type = getErrorType(error)
  const message = getErrorMessage(error)

  logPaymentError(type, error, context)
  toast.error(message, TOAST_CONFIG)
}

/**
 * Displays a successful payment notification.
 *
 * @param {string} [message='Pagamento completato con successo']
 */
export const handlePaymentSuccess = (
  message = 'Pagamento completato con successo'
) => {
  toast.success(message, {
    ...TOAST_CONFIG,
    autoClose: 3000,
  })

  console.log('[Payment Success]', {
    message,
    timestamp: new Date().toISOString(),
  })
}

/**
 * Logic to determine if a failed operation should be retried.
 *
 * @param {Error|Object} error
 * @returns {boolean}
 */
export const isRetryableError = (error) => {
  const type = getErrorType(error)
  return [
    PAYMENT_ERROR_TYPES.NETWORK_ERROR,
    PAYMENT_ERROR_TYPES.TIMEOUT_ERROR,
    PAYMENT_ERROR_TYPES.GATEWAY_ERROR,
  ].includes(type)
}

const paymentErrorHandler = {
  handlePaymentError,
  handlePaymentSuccess,
  isRetryableError,
  getErrorMessage,
  getErrorType,
}

export default paymentErrorHandler
